import { Controller } from "@hotwired/stimulus"
import DataLoader from "#lib/data_loader"
import { emitEquipmentCost, strictNum } from "#lib/equipment_cost"
import { toTree } from "#lib/html_form_controls_tree"
import { tinyid } from "#lib/tinyid"
import JsonFetch from "#lib/json_fetch"
import { coalesce } from "#lib/coalesce"
import { PendingItems } from "#lib/pending_items"

// Shared cost/label helpers the per-type controllers use.
export const qtyNum = (v) => (/^\d+$/.test(String(v ?? "").trim()) ? Number(v) : 1)
export const timesQty = (f) => strictNum(f.cost) * qtyNum(f.qty) // cost × qty (qty->1 when absent)
export const titlecase = (s) => (s || "").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase())
export const join = (...parts) => parts.filter(Boolean).join(" · ")

// Shared bases for the CP2020 sheet COLLECTIONS — the equipment areas (gear/weapons/
// cyberware/armor/chips) and skills. Each area is a COLLECTION (one <fieldset>) of
// ITEMS (nested <fieldset>s); both come in a per-type flavour that extends these
// bases and implements only what's unique — GearItem#cost(), ArmorItem#entry()/
// #spAt(), CyberwareCollection#statBonuses(), SkillsCollection's grouping/reconcile,
// etc. (skills has no cost — capability present only where true). There is NO generic
// delegation table and NO query-string interface: an item ANSWERS FOR ITSELF with
// real methods, and a peer enumerates a collection and calls them:
//
//   armorCollection.getAll().map((i) => i.cost()).reduce(sum)
//   armorCollection.get("kevlar").spAt("head")
//
// The collection is a pure ENUMERATOR — it holds no domain knowledge; get()/getAll()
// hand back item controllers and the caller asks them questions.

// A single equipment ITEM — one <fieldset> of named controls. Subclasses add domain
// methods (cost/entry/…). The base owns only what every item shares: reading its own
// controls, its toggle cells, and its lifecycle.
export class Item extends Controller {
  static values = { slug: String }

  // On connect the item HYDRATES ITSELF IN PLACE. The collection already placed this
  // element in its FINAL DOM home (renderItem — synchronous, before Stimulus connected
  // us) and registered the real entry as a live JS object in its pending-map, leaving only
  // an opaque data-hydration-id on our root (NO data stuffed in the DOM — just a handle).
  // We claim that object (consume-once) and fill our own controls/spans from it.
  //
  // Placement happens BEFORE connect, on purpose: Stimulus rebuilds the controller on ANY
  // post-connect DOM move (an appendChild of an attached node is a remove+add mutation ->
  // disconnect + reconnect -> a fresh instance, losing everything hydration set). So the
  // item must NEVER move itself after connecting. It connects once, in its final spot, and
  // its hydration-built state persists. The collection owns WHERE (it knows the entry's
  // shape); the item owns FILL. See the place-before-connect note on Collection#renderItem.
  //
  // Why fill on the item at all: an item must be QUERYABLE for what it holds (cost/entry/…),
  // and owning its own fill is what lets serialize() read the item's own state (the stash)
  // rather than whatever template happened to render — so entry() is template-independent.
  //
  // The template flags its filler via data-fill on the root: "show" -> fillShow, else fill.
  // Async: fills read the catalog. After the fill resolves, run the post-fill lifecycle.
  connect() {
    const hydration = this.collection?.takeHydration(this.element.dataset.hydrationId)
    const entry = hydration?.entry ?? {}
    // resolveFilled: the collection's per-item fill-completion deferred (see registerHydration/
    // hydrate). We resolve it after fill so the collection's readiness (_hydrated) means FILLED,
    // not merely placed. Held on the instance so afterFill resolves it even when fill throws.
    this._resolveFilled = hydration?.resolveFilled
    // Seed the stash from the entry — the item's own copy of its stored data, as real JS
    // props with native types preserved (the entry IS what serialized in). get()/set() are
    // the accessors; the fill builder BINDS each key to its DOM target (see builder()), so
    // set() keeps stash + DOM in sync.
    this._stash = { ...entry }
    this._bindings = new Map() // key -> { el, kind: "control"|"checkbox"|"text" }
    const filler = this.element.dataset.fill === "show" ? this.fillShow : this.fill
    Promise.resolve(filler.call(this, entry)).finally(() => this.afterFill())
  }

  // NOTE: there is deliberately no base entry(). Each area WRITES ITS OWN, as an explicit
  // object literal, so the blob's shape and the ORIGIN of every key are visible at the one
  // place you'd look for them. A shared `static ENTRY_KEYS` list was tried and reverted: a
  // key list says which keys, but loses where they come from — that slug is a Stimulus
  // value while the rest are stash reads, that skills coerces to int, that armor derives
  // covers[]/sp_<loc>. The bodies look alike, but they aren't shared responsibility; each
  // is one area stating its own serialization contract.
  //
  // What every area's entry() DOES share, and must keep: it reads the STASH, never the DOM
  // (so it's independent of which template variant rendered), over a FIXED key list (the
  // stash also holds catalog display text and derived scratch, so serialization must not
  // drift with fill()).

  get(key) {
    return this._stash?.[key]
  }

  // Update a key: always the stash, and (unless the value CAME FROM the DOM) the bound DOM
  // target too. The fromDom path is the user-edit direction — the control already holds the
  // new value (native change), so re-painting it would be redundant and can disturb the
  // caret/selection; we only capture it into the stash. A programmatic set() (fill, derived
  // recompute) paints the bound target so stash and display stay identical.
  set(key, value, { fromDom = false } = {}) {
    this._stash ??= {}
    this._stash[key] = value
    if (!fromDom) this.paintBinding(key, value)
    return value
  }

  // Write a key's value into its bound DOM target per the binding kind. No-op if unbound
  // (a stash-only key) — the value still lives in the stash.
  paintBinding(key, value) {
    const binding = this._bindings?.get(key)
    if (!binding) return
    const { el, kind } = binding
    if (kind === "checkbox") el.checked = !!value
    else if (kind === "text") el.textContent = value ?? ""
    else el.value = value ?? ""
  }

  // Record key -> (el, kind) so set() knows where to paint, and — for an EDITABLE control
  // (not inside the disabled show fieldset: control.disabled is false) — bind the reverse
  // direction: a user edit captures back into the stash via set(…, { fromDom: true }). A
  // display span (kind "text") and a disabled control get no listener.
  //
  // The listener is on `input` for BOTH controls and checkboxes — deliberately, not `change`
  // for checkboxes. The collection reassembles on the item's `input` action (which reads the
  // stash via entry()); a checkbox's `change` fires AFTER its `input`, so a change-based
  // capture would update the stash too late — reassemble would read the pre-toggle value.
  // Checkboxes fire `input` on user toggle too, so `input` captures in time.
  bind(key, el, kind) {
    if (!el) return
    this._bindings ??= new Map()
    this._bindings.set(key, { el, kind })
    if ((kind === "control" || kind === "checkbox") && !el.disabled) {
      const read = () => (kind === "checkbox" ? el.checked : el.value)
      el.addEventListener("input", () => this.set(key, read(), { fromDom: true }))
    }
  }

  // Post-fill lifecycle: paint toggle cells from their (now-filled) hidden checkboxes, signal
  // fill-completion to the collection (so its readiness resolves — see connect/hydrate), then
  // announce so the collection converges. NO placement here — the collection placed us before
  // we connected (a self-move would rebuild this controller). Runs AFTER the async fill (via
  // .finally, so it runs even if fill threw — readiness must not hang).
  afterFill() {
    for (const cell of this.element.querySelectorAll("[data-loc],[data-check]")) {
      const box = this.checkboxFor(cell)
      this.paintCell(cell, !!box?.checked)
    }
    this._resolveFilled?.()
    this.changed()
  }

  // Fill self from the entry (moved here from each collection's fillItem). Default: no-op.
  // Each area overrides. fillShow defaults to fill (an area with a leaner show template
  // overrides fillShow). Async: fills read the catalog. Uses this.builder() to paint.
  async fill(_entry) {}
  async fillShow(entry) { return this.fill(entry) }

  // A terse fill builder bound to THIS item's element. Each verb does TWO things now: it
  // BINDS the key to its DOM target (so set() can keep them in sync + a user edit of an
  // editable control flows back to the stash) and it applies the initial value THROUGH
  // set() — so the first paint IS a set, one write path. Same call sites as before:
  //   b.fill(entry, { fields, bools, text })  // verbatim entry[name] -> same-named control
  //   b.field("hc", entry.hc_avg)             // renamed / transformed / constant
  //   b.bool("head", covered)                 // list-derived
  //   b.text("name", cat.name)                // display span (data-field), not a control
  // fill() is ONLY flat verbatim passthroughs; anything computed/renamed/derived uses an
  // explicit field/bool/text so its origin is visible at the call site.
  builder() {
    const el = this.element
    // Declared before the verbs so each `return b` closes over an initialized binding —
    // the verbs are chainable, so they must hand back the builder itself.
    const b = { el }
    // field/bool bind by control name; text binds by data-field. Binding by KEY (not the
    // matched element) means set(key, …) later resolves the target itself.
    b.field = (name, value) => {
      this.bind(name, el.querySelector(`[name='${name}']`), "control")
      this.set(name, value)
      return b
    }
    b.bool = (name, on) => {
      this.bind(name, el.querySelector(`[name='${name}']`), "checkbox")
      this.set(name, !!on)
      return b
    }
    b.text = (f, str) => {
      this.bind(f, el.querySelector(`[data-field='${f}']`), "text")
      this.set(f, str)
      return b
    }
    b.fill = (entry, { fields = [], bools = [], text: texts = [] } = {}) => {
      fields.forEach((n) => b.field(n, entry[n]))
      bools.forEach((n) => b.bool(n, entry[n]))
      texts.forEach((n) => b.text(n, entry[n]))
      return b
    }
    return b
  }

  // This item's OWNING collection controller — the nearest ancestor with the collection
  // identity (own identity minus the "-item" suffix). closest() walks up natively; the
  // item asks for its collection BY NAME, but the name is derived from its own type, so no
  // per-area hardcoding. An item knowing its collection's identity is a small, honest
  // coupling — cheaper than an outlet, and the item genuinely belongs to that collection.
  get collection() {
    const collectionIdentity = this.identifier.replace(/-item$/, "")
    const el = this.element.closest(`[data-controller~='${collectionIdentity}']`)
    return el ? this.application.getControllerForElementAndIdentifier(el, collectionIdentity) : null
  }

  // Announce a change; the collection (ancestor) reassembles on the bubbling event.
  // Wired: data-action="input->cp2020--<type>-item#changed".
  changed() {
    this.reflectCost()
    this.dispatch("item-changed", { prefix: "cp2020", bubbles: true })
  }

  // Remove self; the removal bubbles a change so the collection reassembles. Routed through
  // the collection so the pending census stays honest (see Collection#removeItemElement).
  remove() {
    const parent = this.element.parentElement
    const collection = this.collection
    if (collection) collection.removeItemElement(this.element)
    else this.element.remove()
    parent?.dispatchEvent(new CustomEvent("cp2020:item-changed", { bubbles: true }))
  }

  // Toggle the hidden checkbox named by a cell's data-loc/data-check, repaint, reserialize.
  // Generic "cell drives a hidden boolean"; armor coverage uses it. The flip goes THROUGH
  // set() (by the box's name) so the STASH stays in sync — entry() reads the stash, so a
  // bypassing DOM-only flip would serialize stale coverage/flags. set() paints the checkbox;
  // we still repaint the cosmetic cell.
  toggleCheck(event) {
    const box = this.checkboxFor(event.currentTarget)
    if (box) {
      const on = !box.checked
      this.set(box.name, on) // stash + paints box.checked
      this.paintCell(event.currentTarget, on)
    }
    this.changed()
  }

  // If this item shows its cost in a display SPAN (chips), keep it in sync with the
  // computed cost. A no-op for items whose cost is an input / that have no cost().
  reflectCost() {
    const span = this.element.querySelector("span[data-field='cost']")
    if (span && typeof this.cost === "function") span.textContent = String(this.cost())
  }

  checkboxFor(cell) {
    return this.element.querySelector(`input[name='${cell.dataset.loc || cell.dataset.check}']`)
  }

  paintCell(cell, on) {
    cell.className = `text-[10px] uppercase rounded cursor-pointer ${
      on ? "bg-green-600/40 text-gray-100" : "text-gray-500 hover:bg-gray-500/20"
    }`
  }
}

// An equipment AREA — one <fieldset> of item fieldsets + the hidden JSON field that
// submits. A pure enumerator: get()/getAll() return this area's item controllers, in
// DOM order, collected STRUCTURALLY via the fieldset tree (never a document-wide
// outlet). CLIENT-RENDERED: it fetches its stored blob and, per entry, renders a data-free
// item and PLACES it in its final home (renderItem: stamp id-handle + register the entry in
// the pending-map + homeFor(entry).appendChild — all synchronous, BEFORE connect). Then
// each ITEM fills ITSELF on connect (Item#fill, claiming its entry via takeHydration), in
// place. Placement precedes connect on purpose: a post-connect DOM move rebuilds the
// controller (see Item#connect). The collection supplies homeFor() (where) + newEntry() (a
// fresh add's entry) + any collection answers (cyberware#statBonuses); the item supplies
// fill()/fillShow() (how). Registered under the area identity (cp2020--gear/…) so the cost
// aggregator's per-type outlet resolves.
export class Collection extends Controller {
  static targets = ["field", "items", "itemTemplate", "addSelect"]
  // characterValue: the character's to_param path (e.g. "npcs/vasquez"), used to build
  // the per-column blob URL the controller fetches its items from (see loadEntries).
  static values = { character: String }

  // The hydration handshake + the pending half of the item census (see slugs()). A FIELD,
  // not a connect() assignment: an item's connect calls back into takeHydration, and a
  // field initializer runs at construction — so the set exists no matter how the two
  // lifecycles interleave, rather than relying on parent-connects-first ordering.
  _pending = new PendingItems()

  // The compute frame's coalescing mutex — the ONE task, built once per controller
  // instance. Same reasoning as _pending: settle() is reachable from outlet-connect
  // callbacks, which don't promise to run after our own connect().
  _converge = coalesce(async () => {
    await this.whenReady()
    await this.recompute()
  })

  connect() {
    this.constructor.CATALOGS?.forEach((k) => DataLoader.systemPreload(k))
    // Every area client-renders: PULL its items from the blob and render them here, then
    // run a readiness-gated compute (see the frame below).
    this._hydrated = this.hydrate() // the readiness promise converge awaits
    this.settle()
  }

  // === COMPUTE FRAME (universal for every client-rendered collection) ============
  // The control flow is identical across collections — only recompute() (the domain body)
  // differs. settle() debounces onto a frame; _converge (the coalesce'd task above) sleeps
  // until ready then computes, never overlapping itself and collapsing any requests that
  // arrive mid-pass into exactly one re-run; whenReady() is the readiness
  // predicate. A collection with cross-cutting derivation (skills) overrides recompute(); a
  // plain one (gear/equipment) inherits the default (reassemble). NONE re-implement the frame.
  //
  // Readiness is the FIRST STEP OF THE TASK, not a gate the caller orders itself against.
  // That's deliberate: when the wait lived in the caller it had to happen BEFORE the
  // already-running check (the wait yields, so another pass could start and finish during
  // it), an ordering no test could catch if broken. Inside the task there is nothing to
  // mis-order. See #lib/coalesce.

  // Schedule a compute for the next frame with no further trigger. Every item connecting/
  // editing and every sibling broadcast funnels here (wire data-action), so this is the
  // ONE compute entry point. Debounced: a burst collapses to a single converge.
  settle() {
    cancelAnimationFrame(this._raf)
    this._raf = requestAnimationFrame(() => this.converge())
  }

  // Run the domain compute, once ready and serialized. Fire-and-forget from settle()'s
  // frame callback, so a throwing recompute is REPORTED (to window.onerror, like a
  // synchronous throw) rather than surfacing as an unhandled rejection. Returns the run's
  // promise for callers that DO want to await. Falls back to console.error where
  // reportError is absent — the handler itself must never throw, or we'd recreate exactly
  // the unhandled rejection it exists to prevent.
  converge() {
    return this._converge().catch((e) => {
      if (typeof reportError === "function") reportError(e)
      else console.error(e)
    })
  }

  // Readiness: what must be true before a compute may run. Default: the initial hydrate
  // (client-render of the stored rows) has completed — so nothing computes over a HALF-
  // rendered area. A PROMISE, not a polled flag: converge sleeps on it and wakes once. A
  // collection extends readiness by AND-ing more awaitables (each MUST resolve on a real
  // event — an outlet connecting, a catalog promise — so readiness can't hang forever).
  whenReady() {
    return this._hydrated ?? Promise.resolve()
  }

  // The per-collection compute body. Default: serialize the blob (+ cost). Skills
  // overrides with its derivation graph (group/seed/reconcile/upbringing/package).
  async recompute() {
    this.reassemble()
  }

  // The area key ("gear", …) from this controller's identity; the item identity is
  // that plus "-item" (cp2020--gear-item), so getAll can ask for the right item.
  get areaKey() {
    return this.identifier.replace(/^cp2020--/, "")
  }
  get itemIdentity() {
    return `cp2020--${this.areaKey}-item`
  }

  // This area's CONNECTED item controllers, in DOM order — how the collection QUERIES its
  // items (asking one anything requires its controller). It maps the tree's item fieldsets
  // through their controllers; an item whose controller hasn't connected yet is honestly
  // not here. For MEMBERSHIP ("is this slug already present?") use slugs()/has() instead —
  // they add the pending half this view structurally can't see. Never hand-roll an element
  // census to work around it.
  getAll() {
    return toTree(this.element)
      .groups.filter((fs) => fs.matches(`[data-controller~='${this.itemIdentity}']`))
      .map((fs) => this.application.getControllerForElementAndIdentifier(fs, this.itemIdentity))
      .filter(Boolean)
  }

  // The item with this slug (or undefined). Peers ask e.g. get("kevlar").spAt("head").
  get(slug) {
    return this.getAll().find((i) => i.slugValue === slug)
  }

  // MEMBERSHIP, connection-independently:
  //
  //   everything here  =  connected controllers (getAll)  ∪  pending entries
  //
  // getAll() maps fieldsets through their CONTROLLERS, so it cannot see an item that is
  // rendered but whose controller hasn't connected yet (Stimulus builds them async, after
  // placement). Anything asking "is this already here?" MUST use this view, or it will
  // re-add during the window between render and connect — a real duplication bug skills hit
  // and worked around locally with a DOM census. The pending set is precisely the missing
  // half (consume-once: registered at render, claimed at connect), so the base can answer it
  // honestly and no future derivation area has to rediscover the race.
  //
  // getAll() keeps meaning exactly "connected controllers" — the right view for QUERYING
  // items (you need a controller to ask one anything). These two are for membership only,
  // which is why they return slugs rather than a merged item/entry list: the two halves are
  // different shapes (live controllers vs. stored entries) and pretending otherwise would
  // hand callers a list whose elements answer to different interfaces.
  slugs() {
    return new Set(
      [...this.getAll().map((i) => i.slugValue), ...this._pending.entries().map((e) => e.slug)]
        .filter((s) => s != null),
    )
  }

  // Is an item with this slug already here (connected or merely rendered)?
  has(slug) {
    return this.slugs().has(slug)
  }

  // Reassemble on any change/add/remove — every path bubbles cp2020:item-changed here
  // (wire data-action), and each item dispatches it on connect so cold-load + add
  // converge. Blob = each item's entry(); cost total = each item's cost() summed.
  reassemble() {
    if (this.hasFieldTarget) {
      this.fieldTarget.value = JSON.stringify(this.getAll().map((i) => i.entry()))
    }
    emitEquipmentCost(this.areaKey, this.subtotal())
  }

  // The cost aggregator PULLS this via the area outlet. Sum of each item's cost().
  subtotal() {
    return this.getAll().reduce((sum, i) => sum + i.cost(), 0)
  }

  // WHERE a rendered item belongs — its final DOM home. Called by renderItem BEFORE the
  // item connects, so placement never moves a live controller (see renderItem). Default:
  // the flat itemsTarget. A grouping collection (skills) overrides to return the row's
  // cosmetic stat group — deriving the group from the ENTRY (which the collection has at
  // render), NOT from the connected item. This is the one bit of entry-shape knowledge the
  // collection must hold; for the 5 equipment areas it holds none (always itemsTarget).
  homeFor(_entry) {
    return this.itemsTarget
  }

  // === CLIENT-RENDER =============================================================
  // Rails ships ONLY an empty <template> (no populated items); this controller PULLS the
  // stored item JSON, renders + PLACES each item itself (in its final home), and the ITEM
  // fills itself on connect. Every area client-renders; the collection supplies homeFor()
  // (where) + newEntry() (a fresh add's entry); the item supplies fill()/fillShow() (how).

  // Render one item from a full stored ENTRY and PLACE it in its final home — SYNCHRONOUS,
  // and DATA-FREE in the DOM. Clone the template, mint the id, stamp slug + an opaque
  // data-hydration-id, register the real entry (a HydrationObject) in the pending-map, and
  // append into homeFor(entry). Placement happens HERE, before Stimulus connects the item,
  // so the item never moves after connecting (a post-connect move rebuilds the controller
  // and loses hydration state — see Item#connect). The item then claims its entry from the
  // pending-map by id and fills itself in place. No fill here (the item controller doesn't
  // exist yet — Stimulus builds it async once it's in the live DOM). Returns the element.
  renderItem(entry) {
    const el = this.itemTemplateTarget.content.cloneNode(true).querySelector(`[data-controller~='${this.itemIdentity}']`)
    this.registerHydration(el, entry)
    // Place in the FINAL home now — this is what makes the element connectable (live DOM)
    // AND settles its position before connect, so the item never self-moves.
    this.homeFor(entry).appendChild(el)
    return el
  }

  // Stamp a freshly-cloned item element with its slug + the opaque hydration handle, mint/
  // carry its id, and register the real entry as PENDING — everything an item needs to
  // hydrate itself on connect, WITHOUT placing it. renderItem places right after; a
  // subclass with bespoke placement (skills groups its rows) calls this then places itself.
  registerHydration(el, entry) {
    el.setAttribute(`data-${this.itemIdentity}-slug-value`, entry.slug ?? "")
    // Mint the id ONCE, here — the single time/place. Preserve an existing id (cold-load /
    // stored entry); mint when absent (a fresh add, or a LEGACY entry saved before ids).
    // Stamp it into the hidden `id` control where the template has one (areas without it,
    // e.g. chips/skills, simply skip), so entry()/serialize round-trips it unchanged.
    const id = entry.id || tinyid()
    const idInput = el.querySelector("input[name='id']")
    if (idInput) idInput.value = id
    // The handle: only this opaque id rides in the DOM. The payload stays a live object.
    el.dataset.hydrationId = id
    // Fold the (possibly freshly-minted) id back into the entry, so the item's stash —
    // seeded from this entry — carries it. entry() reads id from the stash, not the DOM.
    // For an id-less area (chips/skills) this just adds an ignored key the entry() drops.
    // register() also mints the fill deferred the item resolves once its (async) fill
    // completes — hydrate() awaits all of them, so "hydrated" means placed AND FILLED, not
    // merely placed. That's what a readiness signal (armor-ready, an outlet consumer's first
    // pull) needs: items queryable.
    this._pending.register(id, { ...entry, id })
    return el
  }

  // The item claims its HydrationObject (consume-once): see #lib/pending_items. Consume-once
  // means the pending set's contents are exactly "rendered but not yet connected" — which is
  // what makes slugs() able to see items getAll() structurally can't. A missing id would be a
  // connect with no matching render — a real bug we'd want to see. (Items are placed before
  // connect and never move, so a controller never reconnects and re-claims.)
  takeHydration(id) {
    return this._pending.take(id)
  }

  // Remove an item ELEMENT from this collection. Goes through here (not a bare .remove())
  // so a row dropped before its controller ever connected also drops its pending
  // registration — otherwise the census would keep counting a row that no longer exists,
  // and hydrate would wait on a fill that will never happen. A no-op on the pending set for
  // the normal case (a connected item consumed its registration at connect).
  removeItemElement(el) {
    if (el.dataset.hydrationId) this._pending.forget(el.dataset.hydrationId)
    el.remove()
  }

  // What READINESS means for this area — the one axis on which hydrate legitimately varies:
  //
  //   "filled" (default) — resolve once every item has FILLED, so _hydrated means the items
  //     are queryable. Required by any area a consumer PULLS from (armor-ready + the SP/EV
  //     readouts, the cost aggregator): a consumer that reads a placed-but-unfilled item
  //     silently gets zeros, which is a wrong ANSWER, not a late one.
  //
  //   "placed" — resolve as soon as the rows are in the DOM. For an area whose compute is an
  //     idempotent clear-then-derive graph over live state (skills), where a pass over
  //     not-yet-filled rows is CORRECTED by the re-converge each fill triggers rather than
  //     wrong. Membership — the thing that must never race — is covered by the census
  //     (slugs()/has()), which counts pending rows, so waiting on fills buys nothing here.
  //
  // A flag rather than a hydrate() override, because the two hydrates were otherwise
  // identical: same fetch, same beginCycle, same render loop. Declaring the difference keeps
  // it visible; re-implementing the method hid one meaningful line inside four duplicated ones.
  static READINESS = "filled"

  // Hydrate the area from the stored blob: fetch the entries, then render + place each.
  // Per READINESS, optionally AWAIT every item's fill (each resolves its deferred in
  // afterFill) so _hydrated — and thus whenReady + any armor-ready/consumer signal chained
  // off it — means FILLED, not merely placed. NO compute here: connect's settle() drives the
  // first converge, which awaits this (whenReady) so it never computes a half-rendered area.
  // Guard: if an item never connects (fill can't run) readiness would hang — but every placed
  // item is in the live DOM so Stimulus connects it, and a fill that throws still resolves the
  // deferred (afterFill runs in the fill promise's .finally chain).
  async hydrate() {
    const entries = await this.loadEntries()
    this._pending.beginCycle() // await THIS cycle's fills, not a previous render's
    for (const entry of entries) this.renderItem(entry)
    if (this.constructor.READINESS === "filled") await this._pending.allFilled()
  }

  // The SOURCE of the stored entries — the one seam a future PURE cache slots into
  // (cache-then-fetch), without touching render. For now: fetch the persisted blob from
  // the per-column endpoint (GET /characters/:id/:area). Always-fetch for the pilot; a
  // failed-validation re-render re-fetches the SAVED blob (unsaved-edit loss is a known,
  // accepted pilot limitation — the cache step later closes it).
  async loadEntries() {
    // characterValue is the character's to_param (e.g. "npcs/vasquez"); the blob route
    // is /characters/*id/:blob.
    const { data } = await JsonFetch.get(`/characters/${this.characterValue}/${this.areaKey}`)
    return Array.isArray(data) ? data : []
  }

  // Add: build a fresh entry from the picked slug (id minted in renderItem) and render +
  // place it the SAME way as cold-load; the item fills itself on connect. Subclass supplies
  // the new entry's shape via newEntry(slug) if it needs catalog defaults; the default is
  // just { slug } and the item's fill does the rest.
  async addClient(event) {
    const slug = event.target.value
    if (!slug) return
    // Sync-clear the select value BEFORE the await: slim-select can echo a second
    // identical `change` back-to-back, and the async gap (newEntry/renderItem) would let
    // it re-enter and add twice; emptying now means the echo reads "" and !slug drops it.
    event.target.value = ""
    const entry = await this.newEntry(slug)
    if (entry) this.addEntry(entry)
    this.resetSelect(event.target)
  }

  // Add a fully-RESOLVED entry (the shared render+converge half of a fresh add), decoupled
  // from where the entry came from. addClient resolves a picked slug through newEntry() and
  // hands the result here; a cross-area spawn (cyberware granting armor) resolves its OWN
  // entry from a spec and calls this directly — no slug, no catalog lookup. Same path as
  // cold-load: renderItem places + the item fills itself on connect; then settle() runs the
  // compute frame so derivation areas converge. Returns the placed element.
  addEntry(entry) {
    const el = this.renderItem(entry) // render + place; the item fills itself on connect
    this.settle() // run the compute frame (recompute) — reassemble for plain areas, the
    // full derivation for skills. Not a bare reassemble, so derivation areas converge.
    return el
  }

  // The entry for a freshly-added slug. Default: just the slug (the item's fill reads the
  // catalog for display + seeds). A subclass overrides to seed stored defaults.
  async newEntry(slug) {
    return { slug }
  }

  resetSelect(select) {
    select.value = ""
    select.slimSelectRefresh?.()
  }
}
